import { useEffect, useState, useMemo } from "react"
import {
  Button,
  message,
  Tag,
  Dropdown,
  Tooltip,
  Modal,
  Table,
  DatePicker,
} from "antd"
import { CopyOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"

import apiClient from "../../Utils/apiClient"
import TableBox from "../../components/TableBox/TableBox"
import PanelLayout from "../../Layout/PanelLayout"
import AutoCompleteBox from "../../components/AutoCompleteBox/AutoCompleteBox"
import StatusTag from "../../components/StatusTag/StatusTag"
import { renderDate } from "../../Utils/utils"
import "../Operations/Operations.scss"

const CustomTask = () => {
  const navigate = useNavigate()
  const { RangePicker } = DatePicker
  const [data, setData] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filteredData, setFilteredData] = useState([])

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState([])
  const [exportRecord, setExportRecord] = useState(null)
  const [exportModal, setExportModal] = useState(false)
  const [modalPagination, setModalPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const [filters, setFilters] = useState({
    hostname: "",
    title: "",
    status: "",
    createdAt: "",
    startDate: "",
    endDate: "",
  })

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  }

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize)
  }, [refresh])

  const fetchData = async (
    customPage = pagination.current,
    customPageSize = pagination.pageSize
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        ...filters,
      }
      const res = await apiClient("tasks/custom-task/getall", "POST", params)

      if (res?.success) {
        const { data: resultData, pageData } = res.result

        const sortedData = [...(resultData || [])].sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt)
          }
          return b._id.localeCompare(a._id)
        })

        setData(sortedData)

        setPagination({
          current: pageData.page,
          pageSize: pageData.pageSize,
          total: pageData.total,
        })
      } else {
        message.error("Failed to load custom task data")
      }
    } catch (err) {
      console.error(err)
      message.error("Error fetching data!")
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilter = () => {
    setFilters({
      hostname: "",
      command: "",
      status: "",
      createdAt: "",
      startDate: "",
      endDate: "",
    })
    fetchData(1, pagination.pageSize)
    setRefresh((pre) => !pre)
  }

  const handlePermanentDelete = async () => {
    try {
      const deleteTasks = await apiClient(
        `tasks/custom-task/remove-bulk`,
        "POST",
        { ids: selectedRowKeys }
      )
      if (deleteTasks?.success) {
        message.success(deleteTasks?.message)
        setRefresh((pre) => !pre)
        setSelectedRowKeys([])
      }
    } catch (err) {
      console.log("error from Custom task", err)
    }
  }

  const showDeleteConfirm = () => {
    Modal.confirm({
      title: `Are you sure you want to permanently delete these Task${
        selectedRowKeys.length > 1 ? "s" : ""
      }?`,
      icon: <DeleteOutlined style={{ color: "red" }} />,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No, Cancel",
      onOk: handlePermanentDelete,
    })
  }

  const downloadAsExcel = async (record) => {
    try {
      const XLSX = await import("xlsx")

      const formatted = formatExportData([record])

      const worksheet = XLSX.utils.json_to_sheet(formatted)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Export")

      XLSX.writeFile(workbook, `Custom-Tasks-${record._id}.xlsx`)
      message.success("Excel exported successfully")
    } catch (err) {
      console.error(err)
      message.error("Excel export failed")
    }
  }

  const downloadAsPDF = async (record) => {
    try {
      const jsPDF = (await import("jspdf")).default
      const autoTable = (await import("jspdf-autotable")).default

      const doc = new jsPDF()

      const formatted = formatExportData([record])

      autoTable(doc, {
        head: [exportOptions.header],
        body: formatted.map((row) => Object.values(row)),
      })

      doc.save(`Custom-Tasks-${record._id}.pdf`)
      message.success("PDF exported successfully")
    } catch (err) {
      console.error(err)
      message.error("PDF export failed")
    }
  }

  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo
    fetchData(current, pageSize)
  }

  const handleAdd = () => {
    navigate("/add-customtask")
  }

  const handleEdit = (id) => {
    navigate(`/edit-customtask/${id}`)
  }
  const openExportOptions = (record) => {
    setExportRecord(record)
    setExportModal(true)
  }
  const handleDelete = async (id) => {
    try {
      const res = await apiClient(`tasks/custom-task/remove-byId/${id}`, "POST")
      if (res.success) {
        message.success(res.message || "Deleted successfully")
        fetchData(pagination.current, pagination.pageSize)
      } else {
        message.error("Failed to delete custom task")
      }
    } catch (err) {
      console.error("Delete error:", err)
      message.error("Error deleting custom task")
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedDevices([])
    setModalPagination({
      current: 1,
      pageSize: 5,
      total: 0,
    })
  }
  const handleDeviceClick = (devices = []) => {
    setSelectedDevices([...devices])
    setModalPagination({
      current: 1,
      pageSize: 5,
      total: devices.length,
    })
    setIsModalOpen(true)
  }

  const taskColumns = useMemo(() => {
    return selectedDevices.flatMap(
      (item) =>
        item?.tasks?.map((task) => ({
          ...task,
          hostname: task.hostname,
          serialNumber: item.serialNumber,
        })) || []
    )
  }, [selectedDevices])
  const deviceColumns = [
    { title: "Host Name", dataIndex: "hostname", key: "hostname" },
    { title: "Serial Number", dataIndex: "serialNumber", key: "serialNumber" },
    {
      title: "Command",
      dataIndex: "command",
      key: "command",
      render: (val) => {
        const handleCopy = (e) => {
          e.stopPropagation()
          navigator.clipboard.writeText(val)
          message.success("Copied to clipboard!")
        }

        if (val && val.length > 50) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip title={val}>
                <span style={{ cursor: "pointer" }}>
                  {val.substring(0, 50)}...
                </span>
              </Tooltip>
              <Tooltip title="Copy">
                <CopyOutlined
                  onClick={handleCopy}
                  style={{ cursor: "pointer", color: "#1890ff" }}
                />
              </Tooltip>
            </div>
          )
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{val}</span>
            <Tooltip title="Copy">
              <CopyOutlined
                onClick={handleCopy}
                style={{ cursor: "pointer", color: "#1890ff" }}
              />
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        return <StatusTag status={val} />
      },
    },
    {
      title: "Output",
      dataIndex: "output",
      key: "output",
      render: (val) => {
        if (!val) return "-"
        return (
          <Tooltip title={val}>
            <span style={{ cursor: "pointer" }}>
              {val.length > 80 ? `${val.substring(0, 80)}...` : val}
            </span>
          </Tooltip>
        )
      },
    },
  ]

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (val) => {
        return val || "-"
      },
    },
    {
      title: "Created AT",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Devices",
      dataIndex: "selectedDevices",
      key: "selectedDevices",
      render: (val) => {
        if (!Array.isArray(val)) return null

        const displayHostnames = val.slice(0, 1)
        const extraHostnames = val.slice(1)

        return (
          <>
            {displayHostnames.map((user, index) => (
              <Tooltip title="Click to view details" key={index}>
                <span
                  className="hostname-link"
                  style={{ marginRight: 8 }}
                  onClick={() => handleDeviceClick(val)}
                >
                  {user.hostname}
                </span>
              </Tooltip>
            ))}
            {extraHostnames.length > 0 && (
              <span
                className="hostname-link"
                onClick={() => handleDeviceClick(val)}
              >{`... +${extraHostnames.length}`}</span>
            )}
          </>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        return <StatusTag status={val} />
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "end",
      render: (_, record) => {
        const menuItems = [
          // {
          //   key: "edit",
          //   label: <span>Edit</span>,
          //   onClick: () => handleEdit(record._id)
          // },
          {
            key: "delete",
            label: <span>Delete</span>,
            onClick: () => handleDelete(record._id)
          },
          {
            key: "export",
            label: <span>Export</span>,
            onClick: () => openExportOptions(record)
          },
        ]
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  const formatExportData = (data) => {
    const final = []

    data?.forEach((d) => {
      d?.selectedDevices?.forEach((dev) => {
        if (dev?.tasks && dev.tasks.length > 0) {
          dev.tasks.forEach((task) => {
            final.push({
              title: d.title,
              createdAt: renderDate(dev.createdAt),
              hostname: dev.hostname,
              command: task.command,
              serialNumber: dev.serialNumber,
              status: task.status?.toUpperCase() || "N/A",
            })
          })
        } else {
          final.push({
            title: d.title,
            createdAt: renderDate(dev.createdAt),
            hostname: dev.hostname,
            serialNumber: dev.serialNumber,
            status: dev.status?.toUpperCase() || "N/A",
          })
        }
      })
    })

    return final
  }
  const exportOptions = {
    endpoint: "filter/custom-task",
    method: "POST",
    params: {
      pageSize: pagination.total,
    },
    onExportData: (data) => setFilteredData(data),
    format: formatExportData,
    data: formatExportData(filteredData),
    header: [
      "Task Name",
      "Created Date",
      "Hostname",
      "Command",
      "Serial Number",
      "Task Status",
    ],
    btnTitle: "Export Custom Task",
    fileName: "Custom-tasks",
  }

  const statusCounts = useMemo(() => {
    const counts = { pending: 0, running: 0, completed: 0, failed: 0 }
    for (const row of data) {
      const status = String(row.status || "").toLowerCase()
      if (status.includes("fail")) counts.failed += 1
      else if (status.includes("complet") || status.includes("success"))
        counts.completed += 1
      else if (status.includes("run")) counts.running += 1
      else counts.pending += 1
    }
    return counts
  }, [data])

  const kpis = [
    {
      key: "total",
      label: "Tasks",
      value: pagination.total || data.length,
      tone: "#2563eb",
      soft: "rgba(59, 130, 246, 0.14)",
    },
    {
      key: "pending",
      label: "Pending",
      value: statusCounts.pending,
      tone: "#d97706",
      soft: "rgba(245, 158, 11, 0.16)",
    },
    {
      key: "running",
      label: "Running",
      value: statusCounts.running,
      tone: "#0891b2",
      soft: "rgba(56, 189, 248, 0.14)",
    },
    {
      key: "failed",
      label: "Failed",
      value: statusCounts.failed,
      tone: "#dc2626",
      soft: "rgba(248, 113, 113, 0.14)",
    },
  ]

  return (
    <PanelLayout
      title="Custom tasks"
      subtitle="Queue one-off PowerShell or script jobs on selected devices."
      actions={
        <Button type="primary" onClick={handleAdd}>
          Add Custom Task
        </Button>
      }
    >
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong style={{ fontSize: 17 }}>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>
        <TableBox
          className="dashboard-inventory-table"
          title={`Custom Tasks (${data.length})`}
          rowSelection={rowSelection}
          columns={columns}
          extra={
            <>
              {selectedRowKeys.length > 0 && (
                <div>
                  <Button
                    type="primary"
                    size="small"
                    onClick={showDeleteConfirm}
                    disabled={loading}
                  >
                    <DeleteOutlined /> Delete ({selectedRowKeys?.length})
                    Devices
                  </Button>
                </div>
              )}
            </>
          }
          data={data}
          loading={loading}
          shadow={false}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, pageSize) => {
              handleTableChange({ current: page, pageSize })
            },
            onShowSizeChange: (current, size) => {
              handleTableChange({ current: 1, pageSize: size })
            },
          }}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search Hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
              />
              <AutoCompleteBox
                label="Title"
                name="title"
                className="mb-3 w-100"
                endPoint="custom-task"
                placeholder="Search Title"
                value={filters.title}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, title: value }))
                }
              />

              <AutoCompleteBox
                label="Status"
                name="status"
                className="mb-3 w-100"
                endPoint="custom-task"
                placeholder="Search Status"
                value={filters.status}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value }))
                }
              />

              <div className="mb-3">
                <label className="form-label d-block">Created At</label>
                <DatePicker
                  className="w-100"
                  value={filters.createdAt ? dayjs(filters.createdAt) : null}
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      createdAt: date ? date.format("YYYY-MM-DD") : "",
                    }))
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label d-block">Start & End Date</label>
                <RangePicker
                  className="w-100"
                  value={
                    filters.startDate && filters.endDate
                      ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                      : []
                  }
                  onChange={(dates) =>
                    setFilters((prev) => ({
                      ...prev,
                      startDate:
                        dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "",
                      endDate:
                        dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "",
                    }))
                  }
                />
              </div>
            </div>
          }
          onFilter={() => {
            setPagination((prev) => ({ ...prev, page: 1 }))
            fetchData(1, pagination.pageSize, filters)
          }}
          onClear={handleClearFilter}
          filters={filters}
          exportable
          exportOptions={exportOptions}
        />

      <Modal
        title={`Details: (${selectedDevices.length} devices) (${taskColumns?.length} tasks)`}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={800}
      >
        <Table
          columns={deviceColumns}
          dataSource={taskColumns}
          rowKey={(record, idx) =>
            `${record.hostname}-${record.serialNumber}-${idx}`
          }
          pagination={{
            current: modalPagination.current,
            pageSize: modalPagination.pageSize,
            total: modalPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "15", "20"],
            size: "small",
            onChange: (page, pageSize) => {
              setModalPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
              }))
            },
            onShowSizeChange: (current, size) => {
              setModalPagination((prev) => ({
                ...prev,
                current: 1,
                pageSize: size,
              }))
            },
          }}
        />
      </Modal>
      <Modal
        title="Export Options"
        open={exportModal}
        onCancel={() => setExportModal(false)}
        footer={null}
      >
        <div className="d-flex flex-column gap-3">
          <Button
            type="primary"
            block
            onClick={() => downloadAsExcel(exportRecord)}
          >
            Export as Excel
          </Button>

          <Button block onClick={() => downloadAsPDF(exportRecord)}>
            Export as PDF
          </Button>
        </div>
      </Modal>
    </PanelLayout>
  )
}

export default CustomTask
